import { newApi } from './api'

export type Filters = {
  status: ClaimStatus[]
  insuranceCategories: string[]
  date: { from: string | undefined; to: string | undefined }
}

export type ClaimStatus = 'open' | 'in_progress' | 'waiting' | 'closed'
export const ClaimStatuses = ['open', 'in_progress', 'waiting', 'closed']

export type Claim = {
  case_id: string
  company_id: string | null
  status: ClaimStatus
  created_at: string
  updated_at: string
  title: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  due_date: string
  location: {
    location_id: string
    name: string
  }
  insurance?: {
    category_id: string
    insurance_id: string
    policy_number: string
    main_risk_insured: string | null
    insured_risk_other_specification: string | null
    carrier: {
      logo_url: string
      name: string | null
    }
  }
}

export const getClaims = (
  companyId: string,
  searchQuery: string,
  filters: Filters,
) =>
  newApi.get(`/company/${companyId}/claims`, {
    params: {
      name: searchQuery,
      ...(filters?.status?.length > 0 && {
        status: filters.status.join(','),
      }),
      ...(filters?.insuranceCategories?.length > 0 && {
        insuranceCategories: filters.insuranceCategories.join(','),
      }),
      ...(filters?.date?.from && { dateFrom: filters.date.from }),
      ...(filters?.date?.to && { dateTo: filters.date.to }),
    },
  })

export const getClaimById = (companyId: string, claimId: string) =>
  newApi.get(`/company/${companyId}/claims/${claimId}`)

export type CaseUnreadCount = {
  unread_messages: number
  unread_files: number
  total: number
}

export type UnreadCount = CaseUnreadCount & {
  by_case: Record<string, CaseUnreadCount>
}

export const getUnreadCount = (companyId: string) =>
  newApi.get(`/company/${companyId}/unread-count`)

export type ClaimMessageContact = { email: string; name: string | null }

type ClaimMessageAttachment = {
  id: string
  filename: string
  content_type: string
}

export type ClaimMessage = {
  item_id: string
  inbox_event_id: string
  subject: string
  body: string
  from: ClaimMessageContact | null
  to: ClaimMessageContact[]
  cc: ClaimMessageContact[]
  sent_at: string
  direction: 'inbound' | 'outbound'
  is_unread: boolean
  attachments: ClaimMessageAttachment[]
}

export const getClaimMessages = (
  companyId: string,
  claimId: string,
  params: { search?: string; before?: string },
) => newApi.get(`/company/${companyId}/claims/${claimId}/messages`, { params })

export const sendClaimMessage = (
  companyId: string,
  claimId: string,
  message: {
    subject?: string
    body: string
    file_ids?: string[]
    reply_to_item_id?: string
  },
) => newApi.post(`/company/${companyId}/claims/${claimId}/messages`, message)

export const markClaimMessageRead = (
  companyId: string,
  claimId: string,
  itemId: string,
) =>
  newApi.post(`/company/${companyId}/claims/${claimId}/messages/${itemId}/read`)

export const getClaimMessageAttachmentDownloadLink = (
  companyId: string,
  claimId: string,
  itemId: string,
  attachmentId: string,
) =>
  newApi.get(
    `/company/${companyId}/claims/${claimId}/messages/${itemId}/attachments/${attachmentId}/presigned-url`,
  )

export type ClaimFile = {
  file_id: string
  display_name: string
  type: string
  file_size: number
  created_at: string
  uploaded_by_type: 'customer' | 'admin' | 'system'
  uploaded_by_name: string | null
  is_unread: boolean
}

export type ClaimFilesPageSize = 10 | 20 | 50

export type ClaimFilesResponse = {
  data: ClaimFile[]
  meta: {
    totalItems: number
    pageSize: number
    currentPage: number
  }
}

export const getClaimFiles = (
  companyId: string,
  claimId: string,
  params: { search?: string; page: number; pageSize: ClaimFilesPageSize },
) => newApi.get(`/company/${companyId}/claims/${claimId}/files`, { params })

export type UploadClaimFilesResponse = {
  succeeded: string[]
  failed: string[]
}

export const uploadClaimFiles = (
  companyId: string,
  claimId: string,
  files: File[],
) => {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))

  return newApi.post(`/company/${companyId}/claims/${claimId}/files`, formData)
}

export type ClaimFileDownloadLink = {
  url: string
  expiresAt: string
}

export const getClaimFileDownloadLink = (
  companyId: string,
  claimId: string,
  fileId: string,
) =>
  newApi.get(
    `/company/${companyId}/claims/${claimId}/files/${fileId}/presigned-download-url`,
  )
